/*
 * product-slider.js
 *
 * myPKA, 26.08.2026 — EINMALIGE INITIALISIERUNG STATT EINER PRO RESIZE-MELDUNG.
 *
 * Ausgangslage: `initSection` hat einen ResizeObserver auf die Sektion gelegt
 * und in JEDEM Callback `initSlider(entry.target)` gerufen — also bei jeder
 * Groessenmeldung eine NEUE Swiper-Instanz auf demselben Element, dazu jedes
 * Mal ein neuer Satz mouseover/mouseout-Listener auf allen Karten.
 *
 * Das ist ein Regelkreis: Swiper liest die Containerbreite und schreibt daraus
 * eine Inline-Breite an seine Slides. Diese Schreibung aendert die Groesse der
 * Sektion, der ResizeObserver meldet, es entsteht die naechste Instanz, die
 * wieder schreibt. Gemessen an der Produktseite (Empfehlungen, 5 Karten),
 * offsetWidth je Karte pro Frame protokolliert und die Swiper-Prototypen
 * `update`/`updateSize`/`updateSlides` mitgeschrieben:
 *
 *     375 px  VORHER: 80 Layoutbreiten-Aenderungen je Seitenaufbau,
 *                     201x updateSize + 201x updateSlides, Einschwingen bis 524 ms
 *     1440 px VORHER: 5 Aenderungen, 35 Swiper-Aufrufe
 *
 * Das ist dieselbe Fehlerklasse wie am 25.08. an der PDP-Bildspalte (165
 * Neuberechnungen); dort kam die Rueckkopplung aus einer content-gemessenen
 * Flexspalte, hier aus der Wieder-Initialisierung.
 *
 * Behoben wird die URSACHE, nicht das Symptom: der Slider wird genau EINMAL
 * gebaut. Der ResizeObserver bleibt nur als Startsignal (die Sektion kann beim
 * Ausfuehren des Skripts noch die Breite 0 haben) und trennt sich selbst,
 * sobald eine Breite vorliegt. Spaetere Groessenaenderungen deckt Swiper mit
 * seinem EIGENEN Beobachter ab — dafuer braucht es keine zweite Instanz.
 *
 * Die Hover-Abblendung der Nachbarkarten wird ebenfalls nur einmal verdrahtet.
 * Die Breitenbedingung (> 1100 px) wandert dabei aus der Verdrahtung IN den
 * Handler: vorher entschied die Breite, OB der Listener existiert — wer unter
 * 1100 px geladen und dann vergroessert hat, bekam den Effekt nie. Jetzt
 * entscheidet sie, ob der Effekt LAEUFT, und das Verhalten stimmt an jeder
 * Breite.
 */
(() => {
	const initSlider = (section) => {
		const slider = section.querySelector(".swiper--products");

		if (!slider) return;

		// Guardrail gegen die Rueckkopplung: eine Sektion, ein Swiper.
		if (slider.dataset.mypkaSliderBereit === "1") return;
		slider.dataset.mypkaSliderBereit = "1";

		const numberColumns = slider.dataset.columnsMobile || 1;
		const productsSize = parseInt(slider.dataset.productsSize) || 0;
		const showProductCount =
			slider.dataset.showProductCount === "true" ||
			slider.dataset.showProductCount === true;

		const swiper = new Swiper(slider, {
			loop: false,
			speed: 800,
			spaceBetween: 8,
			allowTouchMove: true,
			slidesPerView: 1,
			slidesPerGroup: 1,
			mousewheel: {
				forceToAxis: true,
			},
			breakpoints: {
				320: {
					slidesPerView: 2,
				},
				750: {
					slidesPerView: 3,
					spaceBetween: 16,
				},
				990: {
					slidesPerView: showProductCount ? 3 : 4,
					spaceBetween: 16,
				},
				1100: {
					slidesPerView: 4,
					spaceBetween: 16,
				},
				1360: {
					slidesPerView: showProductCount ? 4.4 : 4,
					spaceBetween: 16,
				},
			},
			navigation: {
				nextEl: `#${section.id} .swiper-button-next`,
				prevEl: `#${section.id} .swiper-button-prev`,
			},
			// myPKA, 26.08.2026: Swipers A11y-Modul beschriftet die Pfeile sonst
			// mit seinen englischen Vorgabetexten ("Previous slide"/"Next slide")
			// — gemessen im deutschen Shop. Die Texte kommen jetzt aus den
			// vorhandenen Theme-Schluesseln `accessibility.previous_slide` /
			// `accessibility.next_slide` und werden per data-Attribut aus Liquid
			// gereicht; ein Asset kann `t` nicht selbst aufloesen.
			a11y: {
				prevSlideMessage: slider.dataset.labelPrev || undefined,
				nextSlideMessage: slider.dataset.labelNext || undefined,
			},
			pagination: {
				el: slider.querySelector(".popular-products__pagination"),
				clickable: true,
				type: "custom",
				renderCustom: function (swiper, current, total) {
					let out = "";
					for (let i = 1; i < total + 1; i++) {
						out += `<span class="swiper-pagination-bullet${
							i === current ? " swiper-pagination-bullet-active" : ""
						}" tabindex="0" role="button" aria-label="Go to slide ${i}"></span>`;
					}
					return out;
				},
			},
			on: {
				beforeInit: function (swiper) {
					const slides = slider.querySelectorAll(".swiper-slide");
					const screenWidth = window.innerWidth;

					let requiredSlides = 1;
					if (screenWidth >= 1200) {
						requiredSlides = showProductCount ? 4.3 : 4;
					} else if (screenWidth >= 990) {
						requiredSlides = 3.3;
					} else if (screenWidth >= 750) {
						requiredSlides = 3;
					} else {
						requiredSlides = 2;
					}

					if (slides.length < requiredSlides) {
						swiper.disable();
						const arrows = section.querySelectorAll(
							".swiper-button-prev, .swiper-button-next",
						);
						arrows.forEach(el => (el.style.display = "none"));
					}
				},
				transitionEnd: function (swiper) {
					swiper.navigation.update();
				},
			},
		});

		const alignArrowsToImageHeight = () => {
			const firstImage = section.querySelector(".media img");
			if (!firstImage) return;

			const updateArrowPosition = () => {
				const imageHeight = firstImage.offsetHeight;
				if (imageHeight === 0) return;

				const arrows = section.querySelectorAll(
					".swiper-button-prev, .swiper-button-next",
				);
				arrows.forEach(arrow => {
					arrow.style.top = `${imageHeight / 2}px`;
					arrow.style.transform = `translateY(-50%)`;
				});
			};

			if (firstImage.complete) {
				updateArrowPosition();
			} else {
				firstImage.addEventListener("load", updateArrowPosition);
			}

			window.addEventListener("resize", updateArrowPosition);
		};

		alignArrowsToImageHeight();

		const shadowEl = section.querySelector(".popular-products__content");

		const updateShadow = () => {
			if (!swiper.isEnd) {
				shadowEl.classList.add("slider-shadow-enable");
				shadowEl.classList.remove("slider-shadow-enable__left");
			} else {
				shadowEl.classList.remove("slider-shadow-enable");
				shadowEl.classList.add("slider-shadow-enable__left");
			}
		};

		if (shadowEl?.dataset?.enableSliderShadow === "true") {
			swiper.on("slideChange", updateShadow);
			swiper.on("reachEnd", updateShadow);
			swiper.on("fromEdge", updateShadow);
		}
	};

	// Nachbarkarten abblenden, solange die Maus auf einer Karte steht.
	// Einmal verdrahtet; die Breitenbedingung steht IM Handler, nicht um ihn
	// herum, damit der Effekt beim Vergroessern des Fensters nicht fehlt.
	const initHover = (section) => {
		if (section.dataset.mypkaHoverBereit === "1") return;
		section.dataset.mypkaHoverBereit = "1";

		const productItems = section.querySelectorAll(".popular-products__item");

		productItems.forEach(item => {
			item.addEventListener("mouseover", () => {
				if (window.innerWidth <= 1100) return;
				productItems.forEach(otherItem => {
					if (otherItem !== item) {
						otherItem.classList.add("popular-products__item--inactive");
					}
				});
			});

			item.addEventListener("mouseout", () => {
				productItems.forEach(other => {
					other.classList.remove("popular-products__item--inactive");
				});
			});
		});
	};

	const initSection = (section) => {
		if (!section || !section.classList.contains("section-product-slider")) return;

		initHover(section);

		// Der ResizeObserver ist hier NUR das Startsignal: beim Ausfuehren des
		// Skripts kann die Sektion noch die Breite 0 haben. Sobald eine Breite
		// vorliegt, wird einmal gebaut und der Beobachter getrennt — alles
		// Weitere macht Swipers eigener Beobachter.
		const ro = new ResizeObserver(entries => {
			for (const entry of entries) {
				if (entry.contentRect.width > 0) {
					ro.disconnect();
					initSlider(entry.target);
					return;
				}
			}
		});

		ro.observe(section);
	};

	initSection(document.currentScript.parentElement);

	document.addEventListener("shopify:section:load", function (event) {
		initSection(event.target);
	});
})();
